import { motion } from "framer-motion";
import { Link } from "react-router-dom";

/**
 * Shared layout for legal documents (Terms, Privacy, Cookies).
 * Takes a `meta` object + a `sections` array of { heading, body | list }.
 * Content here is placeholder boilerplate — Lexafide legal counsel should replace
 * the operative paragraphs with their reviewed copy before publication.
 */
const LegalPage = ({ meta, sections }) => {
    return (
        <main data-testid="legal-page" className="relative">
            {/* Hero */}
            <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 border-b border-[var(--border)]">
                <div className="max-w-3xl mx-auto px-6 md:px-10">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <span className="eyebrow-pill">{meta.kicker}</span>
                        <h1 className="font-display text-[clamp(2.4rem,5.4vw,4.4rem)] leading-[1.04] tracking-tight font-semibold text-[var(--ink)] mt-5">
                            {meta.title}
                        </h1>
                        <p className="text-[var(--ink-soft)] mt-4 text-lg">{meta.intro}</p>
                        <p className="mt-6 font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--ink-dim)]">
                            Last updated · {meta.lastUpdated}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Sections */}
            <section className="py-12 md:py-20">
                <div className="max-w-3xl mx-auto px-6 md:px-10 space-y-12">
                    {sections.map((s, i) => (
                        <motion.section
                            key={s.heading}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.4, delay: i * 0.04 }}
                            data-testid={`legal-section-${i}`}
                        >
                            <h2 className="font-display text-2xl md:text-[1.75rem] leading-tight font-semibold text-[var(--ink)] mb-4">
                                {String(i + 1).padStart(2, "0")}. {s.heading}
                            </h2>
                            {s.body && (
                                <p className="text-[var(--ink-soft)] leading-relaxed text-[15px] whitespace-pre-line">
                                    {s.body}
                                </p>
                            )}
                            {s.list && (
                                <ul className="mt-4 space-y-2.5 text-[var(--ink-soft)] text-[15px]">
                                    {s.list.map((item) => (
                                        <li key={item} className="flex items-start gap-3 leading-relaxed">
                                            <i className="fa-solid fa-check text-[10px] mt-1.5 accent-blue flex-shrink-0" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </motion.section>
                    ))}

                    {/* Contact footer */}
                    <motion.section
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4 }}
                        className="pt-8 mt-12 border-t border-[var(--border)]"
                    >
                        <p className="text-[var(--ink-soft)] text-[15px]">
                            Questions about this policy? Email{" "}
                            <a
                                href="mailto:legal@lexafide.com"
                                data-testid="legal-contact-email"
                                className="accent-blue font-medium link-grow"
                            >
                                legal@lexafide.com
                            </a>{" "}
                            or{" "}
                            <Link to="/contact" className="accent-blue font-medium link-grow">
                                reach our team
                            </Link>
                            .
                        </p>
                    </motion.section>
                </div>
            </section>
        </main>
    );
};

export default LegalPage;
